import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft, faBoxOpen, faCalendarAlt, 
    faMapMarkerAlt, faClock, faCheckCircle, faHandshake, faTimesCircle,
    faStar, faRunning, faExclamationTriangle, faCommentDots, faFileInvoice
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';
import InvoiceModal from './InvoiceModal'; 
import OrderActionModal from './OrderActionModal';
import { io } from 'socket.io-client';

const socket = io('https://api.liber.am', {
    auth: { token: localStorage.getItem('token') },
    transports: ['websocket'],
    autoConnect: true // Սարքիր true, որ հենց էջը բացվի՝ միանա
});

const MyOrders = ({ user }) => {
    const { t } = useTranslation(); // ✅ i18n
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hoverRating, setHoverRating] = useState({}); 
    const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
    const [selectedInvoiceOrder, setSelectedInvoiceOrder] = useState(null);
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [selectedActionData, setSelectedActionData] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const isProcessing = useRef(false);

    const goldColor = '#c5a059';
    const activeFilter = location.state?.filter || 'current';
    const token = localStorage.getItem('token');

    const axiosConfig = {
        headers: { 
            'ngrok-skip-browser-warning': 'true',
            'Authorization': token ? `Bearer ${token}` : ''
        }
    };

    const loadOrders = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/user-orders/${user.id}`, axiosConfig);
            setOrders(res.data);
        } catch (err) {
            console.error("Orders load error:", err);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        if (!socket.connected) socket.connect();
        loadOrders();
        socket.on('order_status_changed', (data) => {
            loadOrders();
        });
        return () => {
            socket.off('order_status_changed');
        };
    }, [loadOrders]);

    const openConfirmModal = (order) => {
        setSelectedActionData({
            type: 'ORDER_FINISHED_APPROVE',
            id: order.id,
            service: order.service_name,
            totalPrice: order.total_price,
            can_pay_later: order.can_pay_later || 0
        });
        setIsActionModalOpen(true);
    };

    const handleModalAction = (actionType, payload) => {
        if (actionType === 'pay_finish') {
            if (isProcessing.current) return;
            
            if (socket && socket.connected) {
                isProcessing.current = true;
                socket.emit('payment_confirmed', {
                    orderId: payload.id,
                    paymentMethod: payload.paymentMethod || 'cash',
                    status: 'finished',
                    rating: payload.rating || 5
                });
                setIsActionModalOpen(false);
                setTimeout(() => { isProcessing.current = false; }, 2000);
            } else {
                alert(t('alert_socket_error'));
            }
        }
    };

    const handleRate = async (orderId, rating) => {
        const order = orders.find(o => o.id === orderId);
        try {
            const res = await axios.post(`${API_BASE}/api/reviews`, {
                orderId: orderId,
                subservice_id: order.subservice_id || 0,
                service_id: order.service_id || 0,
                name: user.full_name || user.name || t('customer_default_name'),
                text: `${t('rating_for_order')} #${orderId}`,
                stars: rating
            }, axiosConfig);

            if (res.data.success) {
                setOrders(prev => prev.map(o => o.id === orderId ? { ...o, rating: rating } : o));
                alert(t('alert_rating_success'));
            }
        } catch (err) {
            alert(t('alert_rating_error'));
        }
    };

    const handleCancel = (orderId) => {
        if (window.confirm(t('confirm_cancel_order'))) {
            axios.put(`${API_BASE}/api/orders/status/${orderId}`, { status: 'cancelled' }, axiosConfig)
            .then(() => {
                alert(t('alert_cancel_success'));
                loadOrders();
            })
            .catch(err => {
                alert(t('alert_cancel_error'));
                console.error(err);
            });
        }
    };

    const openInvoice = (order) => {
        setSelectedInvoiceOrder({
            id: order.id,
            service: order.service_name,
            totalPrice: order.total_price,
            userName: user.full_name || user.name,
            address: order.location,
            date: order.order_date
        });
        setIsInvoiceOpen(true);
    };

    const getStatusDetails = (status) => {
        const s = (status || 'new').toLowerCase().trim();
        switch(s) {
            case 'new': return { label: t('status_new'), icon: faClock, color: '#f59e0b', bg: '#fef3c7' };
            case 'processing': return { label: t('status_processing'), icon: faBoxOpen, color: '#d97706', bg: '#fff7ed' };
            case 'confirmed': return { label: t('status_confirmed'), icon: faHandshake, color: '#2563eb', bg: '#eff6ff' };
            case 'arrived': return { label: t('status_arrived'), icon: faRunning, color: '#7c3aed', bg: '#f5f3ff' };
            case 'pending_finish': return { label: t('status_pending_pay'), icon: faExclamationTriangle, color: '#16a34a', bg: '#f0fdf4' };
            case 'finished':
            case 'completed': return { label: t('status_finished'), icon: faCheckCircle, color: '#16a34a', bg: '#f0fdf4' };
            case 'cancelled': return { label: t('status_cancelled'), icon: faTimesCircle, color: '#64748b', bg: '#f1f5f9' };
            default: return { label: t('status_processing'), icon: faBoxOpen, color: '#94a3b8', bg: '#f8fafc' };
        }
    };

    const filteredOrders = orders.filter(order => {
        const s = (order.status || 'new').toLowerCase().trim();
        if (activeFilter === 'current') return !['finished', 'completed', 'cancelled'].includes(s);
        if (activeFilter === 'completed') return ['finished', 'completed'].includes(s);
        if (activeFilter === 'cancelled') return s === 'cancelled';
        return true;
    });

    if (!user) return <div style={emptyPageStyle}>{t('please_login')}</div>;

    return (
        <div style={pageStyle}>
            <div style={containerStyle}>
                <button onClick={() => navigate('/profile')} style={backBtnStyle}>
                    <FontAwesomeIcon icon={faArrowLeft} /> {t('back_to_profile')}
                </button>

                <div style={headerFlexStyle}>
                    <h2 style={titleStyle}>
                        {activeFilter === 'current' ? t('tab_current') : 
                         activeFilter === 'completed' ? t('tab_completed') : t('tab_cancelled')}
                    </h2>
                    <span style={countBadgeStyle}>
                        {filteredOrders.length} {t('orders_count')}
                    </span>
                </div>

                {loading ? (
                    <div style={emptyStyle}>{t('loading')}...</div>
                ) : (
                    <div style={listStyle}>
                        {filteredOrders.length === 0 ? (
                            <div style={emptyCardStyle}>{t('no_orders_in_section')}</div>
                        ) : (
                            filteredOrders.map(order => {
                                const status = getStatusDetails(order.status);
                                const isDone = ['finished', 'completed'].includes((order.status || '').toLowerCase());
                                const isPendingPay = (order.status || '').toLowerCase() === 'pending_finish';

                                return (
                                    <div key={order.id} style={orderCardStyle}>
                                        <div style={cardHeaderStyle}>
                                            <div style={{flex: 1}}>
                                                <h3 style={serviceNameStyle}>{order.service_name}</h3>
                                                <div style={dateRowStyle}>
                                                    <FontAwesomeIcon icon={faCalendarAlt} style={{color: goldColor}} />
                                                    {new Date(order.order_date).toLocaleDateString()} | {order.order_time}
                                                </div>
                                            </div>
                                            <div style={{...statusBadge, background: status.bg, color: status.color}}>
                                                <FontAwesomeIcon icon={status.icon} /> {status.label}
                                            </div>
                                        </div>

                                        <div style={cardBodyStyle}>
                                            <p style={locationStyle}>
                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{marginRight: '8px', color: '#64748b'}} /> 
                                                {order.location || t('no_address')}
                                            </p>
                                            
                                            {isDone && (
                                                <div style={ratingSectionStyle}>
                                                    <p style={ratingTitleStyle}>
                                                        {order.rating > 0 ? t('your_rating') : t('rate_work')}
                                                    </p>
                                                    <div style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
                                                        {[1, 2, 3, 4, 5].map(star => (
                                                            <FontAwesomeIcon 
                                                                key={star}
                                                                icon={faStar}
                                                                onMouseEnter={() => !order.rating && setHoverRating({ ...hoverRating, [order.id]: star })}
                                                                onMouseLeave={() => !order.rating && setHoverRating({ ...hoverRating, [order.id]: 0 })}
                                                                onClick={() => !order.rating && handleRate(order.id, star)}
                                                                style={{
                                                                    cursor: order.rating ? 'default' : 'pointer',
                                                                    color: star <= (hoverRating[order.id] || order.rating) ? goldColor : '#e2e8f0',
                                                                    fontSize: '24px'
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div style={cardFooterStyle}>
                                            <div style={priceContainer}>
                                                <span style={{fontSize: '12px', color: '#64748b'}}>{t('total_price_label')}՝</span>
                                                <span style={priceStyle}>{order.total_price || '0'} ֏</span>
                                            </div>
                                            
                                            <div style={{display: 'flex', gap: '10px'}}>
                                                {isPendingPay && (
                                                    <button onClick={() => openConfirmModal(order)} style={confirmBtnStyle}>
                                                        {t('btn_pay')}
                                                    </button>
                                                )}
                                                {isDone && (
                                                    <button onClick={() => openInvoice(order)} style={invoiceBtnStyle}>
                                                        <FontAwesomeIcon icon={faFileInvoice} /> {t('btn_invoice')}
                                                    </button>
                                                )}
                                                {['new', 'processing'].includes((order.status || '').toLowerCase()) && (
                                                    <button onClick={() => handleCancel(order.id)} style={cancelBtnStyle}>
                                                        {t('btn_cancel')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            <InvoiceModal 
                isOpen={isInvoiceOpen} 
                onClose={() => setIsInvoiceOpen(false)} 
                orderData={selectedInvoiceOrder} 
            />
            {isActionModalOpen && (
                <OrderActionModal 
                    isOpen={isActionModalOpen} 
                    data={selectedActionData} 
                    onAction={handleModalAction}
                    onClose={() => setIsActionModalOpen(false)} 
                />
            )}
        </div>
    );
};

// --- Styles (Մնում են նույնը) ---
const confirmBtnStyle = { background: '#16a34a', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const invoiceBtnStyle = { background: '#eff6ff', color: '#2563eb', border: '1px solid #dbeafe', padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' };
const cancelBtnStyle = { background: '#fff', color: '#ef4444', border: '1px solid #fee2e2', padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const pageStyle = { padding: '120px 20px 80px', background: '#fcfcfc', minHeight: '100vh' };
const containerStyle = { maxWidth: '650px', margin: '0 auto' };
const titleStyle = { fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 };
const headerFlexStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' };
const countBadgeStyle = { background: '#0f172a', color: '#fff', padding: '6px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: '600' };
const backBtnStyle = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', marginBottom: '20px', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' };
const listStyle = { display: 'flex', flexDirection: 'column', gap: '20px' };
const orderCardStyle = { background: '#fff', padding: '25px', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' };
const cardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' };
const serviceNameStyle = { fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: '0 0 5px 0' };
const dateRowStyle = { fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' };
const statusBadge = { padding: '6px 12px', borderRadius: '10px', fontWeight: '700', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' };
const cardBodyStyle = { marginBottom: '20px' };
const locationStyle = { fontSize: '14px', color: '#475569', marginBottom: '12px' };
const cardFooterStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f1f5f9' };
const priceContainer = { display: 'flex', flexDirection: 'column' };
const priceStyle = { fontSize: '18px', fontWeight: '800', color: '#0f172a' };
const ratingSectionStyle = { marginTop: '20px', padding: '20px', background: '#fcfcfc', borderRadius: '20px', border: '1px solid #f1f5f9', textAlign: 'center' };
const ratingTitleStyle = { fontSize: '14px', marginBottom: '12px', color: '#0f172a', fontWeight: '700' };
const emptyCardStyle = { textAlign: 'center', padding: '50px', color: '#94a3b8', background: '#f8fafc', borderRadius: '24px', border: '2px dashed #e2e8f0' };
const emptyPageStyle = { padding: '200px', textAlign: 'center', fontWeight: '700', color: '#64748b' };
const emptyStyle = { textAlign: 'center', padding: '80px', color: '#94a3b8' };

export default MyOrders;